import { TellescopeModelFromName, type useTellescopeMutation } from "@tellescope/tanstack-query";
import type { FormFieldNode, NextFieldLogicOptions } from "../../utils/types";
import { useCallback, useMemo } from "react";
import { FormTypesWithValues, TellescopeFieldResponse } from "../../types";
import {
  AnswerForType,
  DatabaseRecord,
  Enduser,
  FormField,
  FormFieldOptions,
  FormResponseValue,
} from "@tellescope/types-models";
import {
  useTellescopeFormCore,
  type LocalTellescopeFormResponseMap,
  type SubmitPhase,
} from "../use-tellescope-form-core";
import { useGetFieldProps, type RepeatInputProps } from "./use-get-field-props";
import { useFormNavigation } from "./use-form-navigation";
import { useAutoAdvance } from "./use-auto-advance";
import { useSaveFieldResponse } from "./use-save-field-response";
import { useEnduserSubmitForm } from "./use-enduser-submit-form";
import { isNextDisabled } from "./is-next-disabled";

export interface UseTellescopeEnduserFormArgs {
  formId: string;
  formTitle?: string;
  responseId?: string;
  enduserId: string;
  parentResponseId?: string;
  rootResponseId?: string;
  startingFieldId?: string;
  existingResponses?: FormResponseValue[];
  urlLogicValue?: string;
  accessCode: string;
  enduser: TellescopeModelFromName<"endusers"> | Enduser;
  publicIdentifier?: string;
}

export interface UseTellescopeEnduserFormReturn {
  currentResponses: LocalTellescopeFormResponseMap;
  validateField: <T extends keyof AnswerForType = keyof AnswerForType>(args: {
    fieldId: string;
    valueOverride?: AnswerForType[T];
  }) => Promise<string | null>;
  validateAllFields: () => Promise<boolean>;
  visibleFields: (FormField & { answer: TellescopeFieldResponse["answer"] })[];
  setFieldValue: <T extends FormTypesWithValues>(fieldId: string, value: AnswerForType[T]) => void;
  saveResponse: () => Promise<void>;
  submitForm: () => Promise<unknown>;
  submitFormMutation: ReturnType<typeof useTellescopeMutation<"form_responses", "submit_form_response">>;
  submitPhase: SubmitPhase;
  isAutoSubmitting: boolean;
  autoSubmitted: boolean;
  autoSubmitError: boolean;
  uploadProgress: { completed: number; total: number };
  form: TellescopeModelFromName<"forms"> | undefined;
  fieldErrors: Record<string, string | null>;
  touchedFields: Set<string>;
  handleFieldBlur: (fieldId: string) => void;
  getFieldProps: <T extends FormTypesWithValues = FormTypesWithValues>(
    fieldId: string,
    type: T
  ) => {
    id: string;
    type: T;
    title: string;
    value: AnswerForType[T];
    onChange: (newValue: AnswerForType[T]) => void;
    onBlur?: () => void;
    error: string | null;
    touched: boolean;
    options?: FormFieldOptions;
    fullZIP?: boolean;
    repeat?: RepeatInputProps;
    disabled?: boolean;
    pendingFiles?: File[];
    onPendingFilesChange?: (files: File[]) => void;
  } | null;
  enduser: TellescopeModelFromName<"endusers"> | Enduser | undefined;
  isComplete: boolean;
  isSubmitted: boolean;
  activeField: FormFieldNode;
  currentFieldResponse: TellescopeFieldResponse | undefined;
  goToNextField: () => Promise<void>;
  goToPreviousField: () => void;
  isNextDisabled: boolean;
  isPreviousDisabled: boolean;
  showSubmit: boolean;
  currentPageIndex: number;
  totalPages: number;
  progress: number;
  getNumberOfRemainingPages: () => number;
  saveFieldResponse: () => Promise<void>;
  fieldsArr: TellescopeModelFromName<"form_fields">[];
  logicOptions: NextFieldLogicOptions;
  applyDatabasePrepopulation: (records: DatabaseRecord[]) => void;
  setCustomerId: (customerId: string) => void;
}

export const useTellescopeEnduserForm = ({
  formId,
  formTitle,
  responseId,
  enduserId,
  parentResponseId,
  rootResponseId,
  startingFieldId,
  existingResponses,
  urlLogicValue,
  accessCode,
  enduser,
  publicIdentifier,
}: UseTellescopeEnduserFormArgs): UseTellescopeEnduserFormReturn | null => {
  const core = useTellescopeFormCore({
    formId,
    formTitle,
    responseId,
    enduserId,
    parentResponseId,
    rootResponseId,
    existingResponses,
    urlLogicValue,
    accessCode,
    enduser,
    publicIdentifier,
  });

  const { form, response } = core.enduserResponseInfoQuery.data ?? {};

  const fieldsMap = useMemo(() => core.fields?.fieldsMap ?? {}, [core.fields?.fieldsMap]);

  const {
    saveActiveFieldAndSubFields,
    saveChargebeeFieldResponse,
    saveFieldResponse: saveFieldResponseFor,
  } = useSaveFieldResponse({ core, responseId, responseAccessCode: response?.accessCode });

  const {
    resolvedActiveField,
    includedFieldIds,
    currentPageIndex,
    totalPages,
    progress,
    goToNextFieldInternal,
    goToPreviousField,
    isPreviousDisabled,
    showSubmit,
    getNumberOfRemainingPages,
  } = useFormNavigation({
    fieldsArr: core.fields?.fieldsArr,
    fieldsMap,
    subFieldIds: core.subFieldIds,
    currentResponses: core.currentResponses,
    responsesMap: core.responsesMap,
    logicOptions: core.logicOptions,
    existingResponses,
    startingFieldId,
    onBeforeAdvance: saveActiveFieldAndSubFields,
  });

  const { setFieldValueWithAutoAdvance, submitFormRef, isAutoSubmitting, autoSubmitted, autoSubmitError } =
    useAutoAdvance({
      setFieldValue: core.setFieldValue,
      fieldsMap,
      currentResponses: core.currentResponses,
      repeatMismatchFieldIds: core.repeatMismatchFieldIds,
      logicOptions: core.logicOptions,
      activeField: resolvedActiveField,
      goToNextFieldInternal,
      saveChargebeeFieldResponse,
    });

  const goToNextField = useCallback(async () => {
    if (!resolvedActiveField) return;

    const error = await core.validateField({ fieldId: resolvedActiveField.value.id });
    if (error) return;

    if (core.repeatMismatchFieldIds.has(resolvedActiveField.value.id)) {
      core.handleRepeatBlur(resolvedActiveField.value.id);
      return;
    }

    goToNextFieldInternal();
  }, [resolvedActiveField, core, goToNextFieldInternal]);

  const nextDisabled = useMemo(
    () =>
      isNextDisabled({
        activeField: resolvedActiveField,
        fieldsMap,
        currentResponses: core.currentResponses,
        fieldErrors: core.fieldErrors,
        repeatMismatchFieldIds: core.repeatMismatchFieldIds,
        logicOptions: core.logicOptions,
        isFieldValueValid: core.isFieldValueValid,
      }),
    [resolvedActiveField, core, fieldsMap]
  );

  const saveFieldResponse = useCallback(
    () => saveFieldResponseFor(resolvedActiveField),
    [saveFieldResponseFor, resolvedActiveField]
  );

  const submitForm = useEnduserSubmitForm({
    core,
    activeField: resolvedActiveField,
    includedFieldIds,
    fieldsMap,
    existingResponses,
    response,
    formId,
    enduserId,
    parentResponseId,
    rootResponseId,
  });

  submitFormRef.current = submitForm;

  const getFieldProps = useGetFieldProps({ core, setFieldValueWithAutoAdvance });

  if (!resolvedActiveField) return null;

  return {
    currentResponses: core.currentResponses,
    validateField: core.validateField,
    validateAllFields: core.validateAllFields,
    visibleFields: core.visibleFields,
    setFieldValue: setFieldValueWithAutoAdvance,
    saveResponse: core.saveResponse,
    submitForm,
    submitFormMutation: core.submitFormMutation,
    submitPhase: core.submitPhase,
    isAutoSubmitting,
    autoSubmitted,
    autoSubmitError,
    uploadProgress: core.uploadProgress,
    form,
    fieldErrors: core.fieldErrors,
    touchedFields: core.touchedFields,
    handleFieldBlur: core.handleFieldBlur,
    getFieldProps,
    applyDatabasePrepopulation: core.applyDatabasePrepopulation,
    setCustomerId: core.setCustomerId,
    enduser: core.enduser,
    isComplete: core.isComplete,
    isSubmitted: !!response?.submittedAt || core.submitFormMutation.isSuccess,
    activeField: resolvedActiveField,
    currentFieldResponse: core.currentResponses[resolvedActiveField.value.id],
    goToNextField,
    goToPreviousField,
    isNextDisabled: nextDisabled,
    isPreviousDisabled,
    showSubmit,
    currentPageIndex,
    totalPages,
    progress,
    getNumberOfRemainingPages,
    saveFieldResponse,
    fieldsArr: core.fields?.fieldsArr ?? [],
    logicOptions: core.logicOptions,
  };
};
